import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Baby, ArrowLeft, Mail, Shield, FileText } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-background pt-8 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Baby className="w-7 h-7 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-2xl">CareKids WI</span>
          </div>
          
          <h1 className="font-heading font-bold text-3xl md:text-4xl mb-2">
            Terms and Conditions
          </h1>
          <p className="text-muted-foreground">
            Last Updated: December 31, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <Card className="card-clay">
          <CardContent className="p-8 prose prose-gray max-w-none">
            <p className="lead text-lg">
              Welcome to <strong>CareKids WI</strong> (&ldquo;Platform&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;). These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of our website, mobile application, and related services (collectively, the &ldquo;Services&rdquo;) designed to support child care management and communication.
            </p>
            
            <p>
              By accessing or using CareKids WI, you agree to be bound by these Terms. If you do not agree, please do not use the Platform.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">1</span>
              Eligibility
            </h2>
            <p>CareKids WI is intended for:</p>
            <ul>
              <li>Licensed child care providers, centers, educators, and administrators</li>
              <li>Parents or legal guardians of enrolled children</li>
            </ul>
            <p>You must be at least <strong>18 years old</strong> and legally able to enter into a binding agreement to use the Platform.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">2</span>
              Account Registration
            </h2>
            <p>To access certain features, you must create an account.</p>
            <p>You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the confidentiality of your login credentials</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p>You are responsible for all activities that occur under your account.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">3</span>
              Platform Purpose
            </h2>
            <p>CareKids WI provides digital tools that may include:</p>
            <ul>
              <li>Child attendance, activity, and progress tracking</li>
              <li>Communication between parents and child care providers</li>
              <li>Enrollment, billing, and administrative management</li>
              <li>Reporting and record-keeping tools</li>
            </ul>
            <p className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <strong>Important:</strong> CareKids WI does not provide child care services. All child care services are delivered by independent third-party providers.
            </p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">4</span>
              Payments and Fees
            </h2>
            <p>If you subscribe to paid features:</p>
            <ul>
              <li>Fees are billed according to your selected plan</li>
              <li>Payments are due as stated at the time of purchase</li>
              <li>Fees are non-refundable unless required by law</li>
            </ul>
            <p>We reserve the right to modify pricing with reasonable advance notice.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">5</span>
              User Responsibilities
            </h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Platform for unlawful, harmful, or abusive purposes</li>
              <li>Upload or share false, misleading, or inappropriate content</li>
              <li>Attempt to gain unauthorized access to systems or data</li>
              <li>Violate any applicable laws, regulations, or licensing requirements</li>
            </ul>
            <p>We may suspend or terminate accounts that violate these Terms.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">6</span>
              Child Data and Privacy
            </h2>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <span><strong>Protecting children&apos;s data is a priority.</strong></span>
              </div>
            </div>
            <ul>
              <li>Personal information is collected and used in accordance with our <strong>Privacy Policy</strong></li>
              <li>Parents or legal guardians consent to the use of their child&apos;s information for Platform functionality</li>
              <li>Providers are responsible for obtaining required parental consents</li>
            </ul>
            <p><strong>CareKids WI does not sell children&apos;s personal data.</strong></p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">7</span>
              Content Ownership
            </h2>
            <ul>
              <li>You retain ownership of the content you submit to the Platform</li>
              <li>You grant CareKids WI a limited, non-exclusive license to use such content solely to operate, maintain, and improve the Services</li>
            </ul>
            <p>We reserve the right to remove content that violates these Terms.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">8</span>
              Third-Party Services
            </h2>
            <p>The Platform may integrate with third-party services (such as payment processors).</p>
            <p>CareKids WI is not responsible for:</p>
            <ul>
              <li>Third-party service performance or availability</li>
              <li>Actions or omissions of third parties</li>
              <li>Disputes between users and third-party providers</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">9</span>
              Service Availability
            </h2>
            <p>We strive to provide reliable access but do not guarantee uninterrupted service.</p>
            <p>We may:</p>
            <ul>
              <li>Perform maintenance or updates</li>
              <li>Modify or discontinue features</li>
              <li>Temporarily suspend access for security or technical reasons</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">10</span>
              Termination
            </h2>
            <p>You may stop using CareKids WI at any time.</p>
            <p>We may suspend or terminate your account if:</p>
            <ul>
              <li>You violate these Terms</li>
              <li>Payments are not made when due</li>
              <li>Required by law or regulation</li>
            </ul>
            <p>Upon termination, your access to the Platform will cease.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">11</span>
              Disclaimers
            </h2>
            <p>The Platform is provided <strong>&ldquo;as is&rdquo;</strong> and <strong>&ldquo;as available.&rdquo;</strong></p>
            <p>CareKids WI makes no warranties regarding:</p>
            <ul>
              <li>Platform availability or performance</li>
              <li>Accuracy of user-provided content</li>
              <li>Suitability of the Platform for specific purposes</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">12</span>
              Limitation of Liability
            </h2>
            <p>To the fullest extent permitted by law, CareKids WI is not liable for:</p>
            <ul>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Loss of data, revenue, or business</li>
              <li>Issues arising from child care services provided by third parties</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">13</span>
              Indemnification
            </h2>
            <p>You agree to indemnify and hold harmless CareKids WI from any claims, damages, or expenses arising from:</p>
            <ul>
              <li>Your use of the Platform</li>
              <li>Violation of these Terms</li>
              <li>Violation of any law or third-party rights</li>
            </ul>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">14</span>
              Changes to These Terms
            </h2>
            <p>We may update these Terms from time to time.</p>
            <p>Updated Terms will be posted on the Platform. Continued use constitutes acceptance of the revised Terms.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">15</span>
              Governing Law
            </h2>
            <p>These Terms are governed by the laws of the <strong>State of Wisconsin</strong>, without regard to conflict-of-law principles.</p>

            <hr className="my-8" />

            <h2 className="flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">16</span>
              Contact Information
            </h2>
            <p>If you have questions about these Terms, please contact us:</p>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="mb-2">
                <strong>Email:</strong>{' '}
                <a href="mailto:info@carekidswi.com" className="text-primary hover:underline">
                  info@carekidswi.com
                </a>
              </p>
              <p className="mb-0">
                <strong>Platform Name:</strong> CareKids WI
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/privacy">
            <Button variant="outline">
              Privacy Policy
            </Button>
          </Link>
          <Link to="/">
            <Button>
              <Baby className="w-4 h-4 mr-2" />
              Go to Home
            </Button>
          </Link>
        </div>

        {/* Additional Links */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            See also our{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
